
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
  noindex?: boolean;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png',
  schema,
  noindex = false,
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;

  useEffect(() => {
    document.title = title;

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }

    // Last-Modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:locale', 'de_DE', true);
    updateMetaTag('og:site_name', 'Seitenschmied', true);

    // Twitter
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', ogImage, true);

    // Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    }

    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) schemaScript.remove();
    };
  }, [title, description, keywords, fullUrl, ogImage, schema, noindex]);
}

// Organization + LocalBusiness Schema
export function getOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return {
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#organization`,
    name: 'Seitenschmied',
    url: siteUrl,
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png',
    },
    image: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png',
    description: 'Moderne Websites für kleine Betriebe, Praxen und Dienstleister im Würmtal und München',
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Gstaller Weg 6',
      addressLocality: 'Gräfelfing',
      postalCode: '82166',
      addressRegion: 'Bayern',
      addressCountry: 'DE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '48.1197',
      longitude: '11.4297',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+49-160-76767763',
      email: 'info@blasiohertz.de',
      contactType: 'customer service',
      availableLanguage: 'German',
    },
    areaServed: [
      { '@type': 'City', name: 'Gräfelfing' },
      { '@type': 'City', name: 'München' },
      { '@type': 'AdministrativeArea', name: 'Würmtal' },
      { '@type': 'Country', name: 'Deutschland' },
    ],
    sameAs: [],
  };
}

// WebPage Schema
export function getWebPageSchema(title: string, description: string, url: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    description,
    inLanguage: 'de-DE',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Seitenschmied',
    },
    publisher: {
      '@id': `${siteUrl}/#organization`,
    },
  };
}

// Service Schema
export function getServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  return {
    '@type': 'ProfessionalService',
    '@id': `${siteUrl}/#service`,
    name: 'Website Erstellung – Seitenschmied',
    serviceType: 'Webdesign & Website Erstellung',
    provider: {
      '@id': `${siteUrl}/#organization`,
    },
    areaServed: {
      '@type': 'Country',
      name: 'Deutschland',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Website Pakete',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Essential Paket',
            description: 'Perfekt für den Start – moderne Website mit allen wichtigen Funktionen',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Professional Paket',
            description: 'Für Betriebe, die mehr wollen – erweiterte Funktionen und Premium-Support',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Enterprise Paket',
            description: 'Die Komplettlösung – maximale Funktionen und individueller Service',
          },
        },
      ],
    },
    url: siteUrl,
  };
}

// BreadcrumbList Schema
export function getBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
